/* eslint-disable max-lines */
import type LogRocket from 'logrocket';
import setupImport from 'logrocket/setup';
import { deepMerge } from './utils.js';

type LogRocketReactSetup = (lr: typeof LogRocket) => void;

const resolveLogRocketReactSetup = (
  mod: Awaited<typeof import('logrocket-react')>,
): LogRocketReactSetup => {
  const logRocketReact = (mod as { default?: unknown }).default ?? mod;
  const setupFn =
    (logRocketReact as { default?: LogRocketReactSetup }).default ??
    logRocketReact;
  if (typeof setupFn !== 'function') {
    throw new TypeError('logrocket-react did not export a setup function');
  }
  return setupFn as LogRocketReactSetup;
};

/**
 * Dynamically loads and initializes the LogRocket React plugin.
 * Deferred so non-React consumers are not bundled against react/react-dom.
 * @param {typeof LogRocket} lr - The LogRocket instance to configure
 */
const loadAndSetupLogRocketReact = async (
  lr: typeof LogRocket,
): Promise<void> => {
  const mod = await import('logrocket-react');
  resolveLogRocketReactSetup(mod)(lr);
};

// Handle both default export (newer) and namespace export (older Node.js)
const setup =
  (setupImport as typeof setupImport & { default?: typeof setupImport })
    .default || setupImport;

// Initialize window.emui namespace
window.emui = window.emui || {};
window.emui.isLogRocketInitialized =
  window.emui.isLogRocketInitialized ?? false;

if (!window.emui.logRocket) {
  // Set _lrAsyncScript and ingestServer only in UMD version when loaded from cdn.mortgagetech.*.ice.com or localhost
  // Localhost URLs are automatically replaced with cdn.mortgagetech.d1.ice.com
  const explicitCdnDomain = window.emui.cdnDomain;
  let cdnDomain: string | null =
    explicitCdnDomain ?? 'https://cdn.mortgagetech.ice.com';
  if (cdnDomain && !cdnDomain.startsWith('https://')) {
    cdnDomain = `https://${cdnDomain.replace(/^http:\/\//, '')}`;
  }
  let useCDNVersion = false;

  // Try to detect script source
  // For classic scripts: use document.currentScript
  // For ES modules: use import.meta.url (SWC will transform for Jest)
  let scriptSrc: string | null = null;
  if (typeof document !== 'undefined' && document.currentScript) {
    scriptSrc = (document.currentScript as HTMLScriptElement).src;
  }
  if (!scriptSrc) {
    try {
      scriptSrc = typeof import.meta !== 'undefined' ? import.meta.url : null;
    } catch {
      // import.meta not available in non-module contexts
    }
  }

  // Extract cdnDomain from scriptSrc if it matches CDN pattern
  if (
    !explicitCdnDomain &&
    scriptSrc &&
    /(cdn\.mortgagetech\.(.*\.)?ice\.com|\/\/localhost[\d:/])/.test(scriptSrc)
  ) {
    try {
      const url = new URL(scriptSrc);
      cdnDomain =
        url.hostname === 'localhost'
          ? 'https://cdn.mortgagetech.d1.ice.com'
          : url.origin;
    } catch {
      // Ignore invalid URL errors
      cdnDomain = null;
    }
  }

  // Set _lrAsyncScript when cdnDomain is available
  if (cdnDomain) {
    useCDNVersion = true;
    // eslint-disable-next-line no-underscore-dangle
    if (!window._lrAsyncScript) {
      // eslint-disable-next-line no-underscore-dangle
      window._lrAsyncScript = `${cdnDomain}/logrocket/logger-1.min.js`;
    }
  }

  window.emui.logRocket = setup({
    ...(useCDNVersion && cdnDomain ? { ingestServer: cdnDomain } : {}),
  });
}

// Same-window dedup: subsequent bundle imports reuse this singleton.
const LogRocketLib = window.emui.logRocket;

let lrAppId: string | null = null;

/**
 * gets the logger instance from window.emui or defaults to console
 * @returns {Console | import('@elliemae/pui-diagnostics').Logger} logger instance
 */
const getLogger = () => window.emui?.logger ?? console;

/**
 * Validates that a string is a full origin (`scheme://host[:port]`) — the form
 * LogRocket's `childDomains` option expects. Bare hostnames like
 * `example.com` are rejected because the SDK matches each candidate frame's
 * `window.location.origin` (which always carries a scheme) against this list.
 * @param {string} value candidate child-domain entry
 * @returns {boolean} `true` when `value` parses to a URL whose `origin` round-trips back to itself.
 */
const isValidOrigin = (value: string): boolean => {
  try {
    return new URL(value).origin === value;
  } catch {
    return false;
  }
};

/**
 * Returns `true` when this page runs as an embedded guest (iframe or popup)
 * rather than as a top-level standalone entry point. LogRocket URL query
 * overrides (`lrAppId`, `analyticsConsent`) are honored only in this context
 * so standalone apps cannot be redirected to an attacker-controlled project.
 * @returns {boolean} Whether the current page is embedded.
 */
const isEmbeddedContext = (): boolean =>
  window !== window.top || window.frameElement != null || window.opener != null;

/**
 * Extracts and validates `lrParentOrigin` from a URL query string.
 * @param {string} search the URL search portion (e.g. `?lrParentOrigin=...`)
 * @returns {string | null} The validated parent origin, or null if absent or invalid.
 */
const readValidParentOrigin = (search: string): string | null => {
  const value = new URLSearchParams(search).get('lrParentOrigin');
  return value && isValidOrigin(value) ? value : null;
};

/**
 * Reads the parent origin from a `lrParentOrigin` query parameter on the
 * current window's URL or, when running inside a same-origin-readable iframe
 * tag, from `window.frameElement.src`. The parent frame sets this via
 * `buildLogRocketQueryParams` when constructing the child iframe URL, giving
 * the child a reliable way to recover its parent's origin even when
 * cross-origin restrictions block `window.parent.location.origin` and
 * `document.referrer` is empty/stripped by `Referrer-Policy`.
 *
 * Invalid (non-origin) values are ignored so a tampered URL cannot inject an
 * arbitrary string into LogRocket's `parentDomain`.
 * @returns {string | null} A validated parent origin, or null if absent or invalid.
 */
const getParentOriginFromQuery = (): string | null => {
  const fromUrl = readValidParentOrigin(window.location.search);
  if (fromUrl) return fromUrl;
  // Old AngularJS microapps that read from frameElement.src in the child
  if (window.frameElement) {
    try {
      const frameUrl = new URL((window.frameElement as HTMLIFrameElement).src);
      return readValidParentOrigin(frameUrl.search);
    } catch {
      // ignore invalid URL errors
    }
  }
  return null;
};

/**
 * Gets the parent domain of the current window.
 *
 * Resolution order:
 * 1. `lrParentOrigin` query parameter (set by the parent via
 *    `buildLogRocketQueryParams`) — the only source that reliably works
 *    cross-origin.
 * 2. `window.parent.location.origin` — works for same-origin parents.
 * 3. `document.referrer` — best-effort cross-origin fallback (may be stripped
 *    by `Referrer-Policy`).
 *
 * For top-level pages (no parent frame) the resolved value is suppressed when
 * it matches the current origin — those pages have no real parent and a
 * self-referential `parentDomain` would tell LogRocket to wait on a parent
 * that doesn't exist. For pages running inside an iframe the value is always
 * returned (even when it equals the current origin) so same-origin children
 * can still link to their parent's session.
 * @returns {string|null} The parent domain origin, or null when top-level with no real parent.
 */
const getParentDomain = () => {
  let parentDomain: string | null = getParentOriginFromQuery();
  if (!parentDomain) {
    try {
      parentDomain = window.parent.location.origin;
    } catch {
      try {
        parentDomain = new URL(document.referrer).origin;
      } catch {
        // Ignore invalid URL errors - parentDomain remains null
      }
    }
  }
  const isTopLevel = window === window.top;
  if (isTopLevel && parentDomain === window.location.origin) {
    return null;
  }
  return parentDomain;
};

/**
 * The one non-origin value LogRocket's `childDomains` accepts: a wildcard that
 * matches any child origin. See {@link buildChildDomains} for when to use it.
 */
const CHILD_DOMAIN_WILDCARD = '*';

/**
 * Always-on resolver for LogRocket's `childDomains` option.
 *
 * Returns at minimum `[<currentOrigin>]` so the SDK is always in cross-origin
 * mode and same-origin frames stitch correctly. Any valid entries supplied
 * via the override are merged in (deduplicated). Callers pass either the
 * value supplied through `init(options)` or, falling back, through
 * `window.emui.logRocketConfig.childDomains`.
 *
 * Override validation:
 * - `null` / `undefined` (not configured): returns `[<currentOrigin>]`
 *   silently.
 * - Any other non-array type (string, object, etc.): ignored with a warning;
 *   returns `[<currentOrigin>]`.
 * - `'*'` anywhere in the array: resolves to exactly `['*']`, matching any
 *   child origin. Use this when child origins are only known at runtime — for
 *   example a multi-tenant host whose guest URL comes from an API response, so
 *   no static list can be written. Every other entry becomes redundant, and the
 *   current origin is not appended, because `'*'` already covers both and the
 *   list doubles as a `postMessage` `targetOrigin` list (duplicate entries mean
 *   duplicate messages).
 * - Any other array entry must be a full origin (`scheme://host[:port]`);
 *   bare hostnames, wildcard patterns like `*.example.com`, or URLs with paths
 *   are dropped with a warning, since LogRocket compares each candidate frame's
 *   `window.location.origin` against this list and only exact matches (or the
 *   bare `'*'`) succeed.
 * @param {unknown} override caller-supplied override (function parameter or window config)
 * @returns {string[]} The resolved child-domain list: `['*']`, or the valid origins plus the current window's origin.
 */
const buildChildDomains = (override: unknown): string[] => {
  const currentOrigin = window.location.origin;
  if (override === undefined || override === null) {
    return [currentOrigin];
  }
  if (!Array.isArray(override)) {
    getLogger().warn(
      `LogRocket childDomains override ignored: must be an array of full origins. Falling back to [${currentOrigin}].`,
    );
    return [currentOrigin];
  }
  if ((override as unknown[]).includes(CHILD_DOMAIN_WILDCARD)) {
    getLogger().info(
      'LogRocket childDomains is set to ["*"]: session linking is allowed with any child origin.',
    );
    return [CHILD_DOMAIN_WILDCARD];
  }
  const { valid, invalid } = (override as unknown[]).reduce<{
    valid: string[];
    invalid: string[];
  }>(
    (acc, entry) => {
      if (typeof entry === 'string' && isValidOrigin(entry)) {
        acc.valid.push(entry);
      } else {
        acc.invalid.push(String(entry));
      }
      return acc;
    },
    { valid: [], invalid: [] },
  );
  if (invalid.length > 0) {
    const dropped = invalid.join(', ');
    getLogger().warn(
      `LogRocket childDomains entries dropped (must be full origins like "https://example.com", or the bare "*" to allow any child origin): ${dropped}`,
    );
  }
  if (valid.includes(currentOrigin)) return valid;
  return [...valid, currentOrigin];
};

/**
 * Resolves LogRocket's `parentDomain` option.
 *
 * Always attempts to populate from auto-detection (`getParentDomain`, which
 * consults the `lrParentOrigin` query param, `window.parent.location.origin`,
 * then `document.referrer`). A valid override (from `init(options)` or, as a
 * fallback, `window.emui.logRocketConfig.parentDomain`) takes precedence; an
 * invalid override (not a full origin) is ignored with a warning so a
 * misconfiguration cannot disable the auto-detected value.
 * @param {unknown} override caller-supplied override (function parameter or window config)
 * @returns {string | null} The resolved parent origin, or null when this is a top-level page (no parent).
 */
const buildParentDomain = (override: unknown): string | null => {
  if (override !== undefined && override !== null) {
    if (typeof override === 'string' && isValidOrigin(override)) {
      return override;
    }
    const overrideLabel = String(override);
    getLogger().warn(
      `LogRocket parentDomain override "${overrideLabel}" ignored: must be a full origin. Using auto-detected value.`,
    );
  }
  return getParentDomain();
};

/**
 * LogRocket initialization options
 */
export type LROptions = Parameters<typeof LogRocket.init>[1] & {
  /**
   * unique identifier of the logrocket project
   * @example 'your-project/your-app'
   * @default window.emui.logRocketConfig.appId
   */
  appId?: string;
  /**
   * indicates if the application is using react framework
   * @default true
   */
  isReact?: boolean;
};

/**
 * Matches the last two dot-separated segments of a hostname (its registrable
 * domain under the naive "eTLD+1" assumption). Compiled once at module load.
 */
const ROOT_HOSTNAME_RE = /[^.]+\.[^.]+$/;

/**
 * Resolves the `rootHostname` for LogRocket cookie scoping
 * (see https://docs.logrocket.com/reference/roothostname).
 *
 * Auto-detected from `window.location.hostname` by taking its last two
 * dot-separated segments, so all same-site cross-origin iframes (e.g.
 * `app.ice.com` and `account.ice.com`) independently compute the
 * same value (`.ice.com`) and merge their sessions via the shared
 * cookie. Cross-site frame linking is handled separately via LogRocket's
 * `parentDomain` / `childDomains` postMessage mechanism.
 *
 * An explicit override (from `init(options)` or, as a fallback,
 * `window.emui.logRocketConfig.rootHostname`) replaces the auto-detected
 * value, but only when it ends with the auto-detected one (i.e. it points
 * to the same site at a deeper or equal subdomain). This lets customers
 * narrow the cookie scope (e.g. `.shared.ice.com`) or work around
 * naive-split limitations on multi-segment public suffixes (e.g. `.co.uk`),
 * while guarding against typos and unrelated-domain overrides.
 * @param {unknown} override caller-supplied override (function parameter or window config)
 * @returns {string | undefined} The root hostname (e.g. '.ice.com'), or undefined when undeterminable.
 */
const getRootHostname = (override: unknown): string | undefined => {
  const match = ROOT_HOSTNAME_RE.exec(window.location.hostname);
  const auto = match ? `.${match[0]}` : undefined;
  if (
    typeof override === 'string' &&
    auto &&
    override.startsWith('.') &&
    override.endsWith(auto)
  ) {
    return override;
  }
  if (override !== undefined && override !== null) {
    const autoLabel = auto ?? '(none)';
    getLogger().warn(
      `LogRocket rootHostname override "${String(
        override,
      )}" ignored: must start with "." and end with the auto-detected "${autoLabel}". Using auto-detected value.`,
    );
  }
  return auto;
};

/**
 * Caller-supplied overrides for the auto-detected fields, resolved by the
 * dedicated validators (`getRootHostname`, `buildChildDomains`,
 * `buildParentDomain`). Each value flows from `init(options)` first, falling
 * back to `window.emui.logRocketConfig.*`.
 */
type AutoDetectedOverrides = {
  rootHostname?: unknown;
  childDomains?: unknown;
  parentDomain?: unknown;
};

/**
 * Generates the default LogRocket initialization options with sanitization and configuration.
 *
 * This function constructs a comprehensive set of default options including:
 * - URL and request/response sanitization to redact sensitive data
 * - DOM recording configuration with hidden attributes and input sanitization
 * - Network request/response sanitizers for security
 * - Cross-domain iframe configuration (childDomains, parentDomain)
 * - Application version and root hostname settings
 * @param {AutoDetectedOverrides} overrides caller-supplied overrides for auto-detected fields
 * @returns {LROptions} The default LogRocket initialization options
 */
const getDefaultOptions = (overrides: AutoDetectedOverrides): LROptions => {
  const childDomains = buildChildDomains(overrides.childDomains);
  const parentDomain = buildParentDomain(overrides.parentDomain);

  return {
    browser: {
      urlSanitizer: (url: string) => {
        let sanitizedUrl = url;
        // redact the value of the auth code query parameter
        sanitizedUrl = sanitizedUrl.replace(
          /([?&])code=([^&]*)/g,
          '$1code=REDACTED',
        );
        // make sure you return the sanitized URL string
        return sanitizedUrl;
      },
    },
    dom: {
      hiddenAttributes: [
        'aria-label',
        'aria-labelledby',
        'aria-describedby',
        'aria-details',
        'aria-errormessage',
        'aria-valuetext',
        'aria-placeholder',
      ],
      inputSanitizer: 'lipsum',
      isEnabled: false,
      textSanitizer: true,
    },
    mergeIframes: true,
    network: {
      requestSanitizer: (request) => {
        const sanitizedHeaders = Object.keys(request.headers).reduce<
          Record<string, string>
        >((acc, key) => {
          acc[key] = /^authorization$/i.test(key)
            ? '**redacted**'
            : request.headers[key] ?? '';
          return acc;
        }, {});

        return {
          ...request,
          headers: sanitizedHeaders,
          body: null,
        };
      },
      responseSanitizer: (response) => {
        if (response.headers['x-secret']) {
          // removes all response data
          return null;
        }

        // scrubs response body
        return {
          ...response,
          body: null,
        };
      },
    },
    release: process?.env?.APP_VERSION, // app sdk based applications use this env variable to specify the app code version
    rootHostname: getRootHostname(overrides.rootHostname),
    childDomains,
    ...(parentDomain ? { parentDomain } : {}),
  };
};

/**
 * Parses the OneTrust active groups string into an array of group IDs.
 * @returns {string[]} Array of active group IDs, or empty array if not available.
 */
const getActiveConsentGroups = (): string[] =>
  window.OnetrustActiveGroups?.split(',')
    .map((g) => g.trim())
    .filter(Boolean) ?? [];

/**
 * Checks if the user has consented to LogRocket initialization by verifying
 * the presence of the 'C0002' consent group in OneTrust active groups.
 * @returns {boolean} `true` if the user has consented (C0002 group is active), `false` otherwise.
 *          Returns `true` if OneTrust is not available (default consent when OneTrust is not loaded).
 */
const hasUserConsentedForLogRocketInit = (): boolean => {
  if (window.OnetrustActiveGroups) {
    return getActiveConsentGroups().includes('C0002');
  }
  return true;
};

/**
 * Determines whether LogRocket should be disabled based on the current environment and configuration.
 *
 * The function checks the following conditions in order:
 * - If the user has not consented to LogRocket initialization (C0002 consent group), LogRocket will be disabled.
 * - If the application is running on 'localhost' and both the environment variable `LOGROCKET_ENABLE_ON_LOCALHOST`
 *   and `window.emui.logRocketConfig.localhostEnable` are not set to `true`, LogRocket will be disabled.
 * - If the global `window.emui.logRocketConfig.enable` is explicitly set to `false`, LogRocket will be disabled.
 * - If the URL query parameter `lrEnabled` is set to `'false'`, LogRocket will be disabled.
 * @returns {boolean} Returns `true` if LogRocket should be disabled, otherwise `false`.
 */
const isLogRocketDisabled = () => {
  if (!hasUserConsentedForLogRocketInit()) {
    return true;
  }
  if (
    window.location.hostname === 'localhost' &&
    process?.env?.LOGROCKET_ENABLE_ON_LOCALHOST !== 'true' &&
    window.emui?.logRocketConfig?.localhostEnable !== true
  ) {
    return true;
  }
  const urlParams = new URLSearchParams(window.location.search);
  if (
    window.emui?.logRocketConfig?.enable === false ||
    urlParams.get('lrEnabled') === 'false'
  ) {
    return true;
  }

  return false;
};

/**
 * Determines whether the user has consented to session recording based on OneTrust configuration and other signals.
 *
 * This function checks for user consent using multiple strategies, in the following order:
 * 1. If `window.emui.dangerouslyOverrideSessionRecordingConsent` is `true`, force consent ON (test/dev only). Any other value (`false`, `undefined`) defers to the normal sources below — `false` is **not** a force-disable.
 * 2. If OneTrust is loaded, checks if `window.OnetrustActiveGroups` contains the session recording group ID ('C0003').
 * 3. If embedded and OneTrust is not loaded, checks the `analyticsConsent`
 *    query parameter in the current URL (set by the parent via
 *    `buildLogRocketQueryParams`).
 * 4. If running in an iframe, checks the `analyticsConsent` query parameter in the iframe's src URL.
 * 5. Defaults to `false` if no consent can be determined.
 *
 * Note: When OneTrust is present, the OneTrust consent groups take precedence over URL parameters.
 * The `analyticsConsent` URL parameter only applies when OneTrust is not loaded.
 * @returns {boolean} `true` if the user has consented to session recording, `false` otherwise.
 */
const hasUserConsentedToSessionRecording = (): boolean => {
  // Only `=== true` short-circuits to grant consent (with a warning). Setting
  // the override to `false` does not force-disable: it falls through so that
  // OneTrust / `analyticsConsent` query / frame fallbacks can still resolve
  // consent normally.
  if (window.emui?.dangerouslyOverrideSessionRecordingConsent === true) {
    getLogger().warn(
      'Using dangerouslyOverrideSessionRecordingConsent - this should only be used for testing purposes',
    );
    return true;
  }

  if (window.OnetrustActiveGroups) {
    return getActiveConsentGroups().includes('C0003');
  }

  if (isEmbeddedContext()) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('analyticsConsent') === 'true') {
      return true;
    }
  }

  // check if queryparameter 'analyticsConsent' is set to 'true' in window.frameElement.src
  // this is to support old angular js microapps running in iframes
  if (window.frameElement) {
    try {
      const frameUrl = new URL((window.frameElement as HTMLIFrameElement).src);
      if (frameUrl.searchParams.get('analyticsConsent') === 'true') {
        return true;
      }
    } catch {
      // ignore invalid URL errors
    }
  }

  return false;
};

/**
 * Retrieves the LogRocket application ID based on a defined precedence:
 * 1. When embedded, if the URL contains a 'lrAppId' query parameter, it is used.
 * 2. Otherwise, the provided `appId` argument is used.
 * 3. If neither is available, attempts to use `window.emui.logRocketConfig.appId`.
 * 4. Returns null if none of the above are found.
 *
 * Top-level standalone pages ignore URL `lrAppId` to prevent telemetry hijacking.
 * @param {string} [appId] - The default LogRocket application ID to use if not specified in the URL or global config.
 * @returns {string | null} The resolved LogRocket application ID as a string.
 */
const getLRAppId = (appId?: string) => {
  const configured = appId ?? window.emui?.logRocketConfig?.appId ?? null;
  if (!isEmbeddedContext()) {
    return configured;
  }
  const lrAppIdFromUrl = new URLSearchParams(window.location.search).get(
    'lrAppId',
  );
  return lrAppIdFromUrl || configured;
};

/**
 * Builds a URL query string containing LogRocket-related parameters.
 *
 * The query parameters include:
 * - `analyticsConsent`: Indicates whether the user has consented to session recording (`'true'` or `'false'`).
 * - `lrEnabled`: Indicates whether LogRocket is enabled based on the global configuration (`'true'` or `'false'`).
 * - `lrAppId`: The LogRocket application ID, or an empty string if not available.
 * - `lrParentOrigin`: The current window's origin, so a child iframe loaded
 *   with this query string can derive its `parentDomain` even when
 *   cross-origin restrictions block `window.parent.location.origin`.
 * @returns {string} The constructed query string with LogRocket parameters.
 */
const buildLogRocketQueryParams = () => {
  const urlParams = new URLSearchParams();
  const consent =
    window.emui?.lrSessionRecordingConsent ??
    hasUserConsentedToSessionRecording();
  const enabled = window.emui?.lrEnabled ?? !isLogRocketDisabled();
  urlParams.append('analyticsConsent', consent ? 'true' : 'false');
  urlParams.append('lrEnabled', enabled ? 'true' : 'false');
  urlParams.append('lrAppId', getLRAppId() ?? '');
  urlParams.append('lrParentOrigin', window.location.origin);
  return urlParams.toString();
};

/**
 * Logs LogRocket initialization status and the resulting session URL once the
 * SDK has established a recording session, via the configured logger
 * (`window.emui.logger` if present, otherwise `console`).
 * @param {boolean} sessionRecordingEnabled whether DOM recording is enabled in this session
 */
const logInitOutcome = (sessionRecordingEnabled: boolean) => {
  getLogger().info(
    `LogRocket initialized ${
      sessionRecordingEnabled ? 'with' : 'without'
    } session recording`,
  );
  LogRocketLib.getSessionURL((sessionURL: string) => {
    getLogger().info(`LogRocket session URL: ${sessionURL}`);
  });
};

/**
 * Builds the auto-detected-overrides bag for `getDefaultOptions`. Caller
 * options take precedence over `window.emui.logRocketConfig.*`; both paths
 * flow through the same validators so an invalid override never reaches
 * LogRocket and the auto-injected current origin is preserved in
 * `childDomains`.
 * @param {AutoDetectedOverrides} fromOptions caller-supplied overrides extracted from `init(options)`
 * @returns {AutoDetectedOverrides} merged overrides with caller > window config precedence
 */
const resolveAutoDetectedOverrides = (
  fromOptions: AutoDetectedOverrides,
): AutoDetectedOverrides => {
  const windowConfig = window.emui?.logRocketConfig;
  return {
    rootHostname: fromOptions.rootHostname ?? windowConfig?.rootHostname,
    childDomains: fromOptions.childDomains ?? windowConfig?.childDomains,
    parentDomain: fromOptions.parentDomain ?? windowConfig?.parentDomain,
  };
};

const finishInitWithOptionalReactPlugin = (
  isReact: boolean,
  sessionRecordingEnabled: boolean,
): void => {
  if (!isReact) {
    logInitOutcome(sessionRecordingEnabled);
    return;
  }
  loadAndSetupLogRocketReact(LogRocketLib)
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      getLogger().warn(
        `Failed to load logrocket-react plugin; React integration disabled. ${message}`,
      );
    })
    .finally(() => {
      logInitOutcome(sessionRecordingEnabled);
    });
};

/**
 * Initializes LogRocket with the provided options.
 *
 * This function performs the core LogRocket initialization logic:
 * - Validates and resolves the LogRocket application ID
 * - Checks if LogRocket is disabled in the current environment
 * - Configures session recording based on user consent
 * - Resolves auto-detected fields (rootHostname, childDomains, parentDomain)
 *   from caller options first, falling back to window.emui.logRocketConfig,
 *   then merges with default options
 * - Initializes LogRocket SDK and optionally React plugin
 * @param {LROptions} [options] - LogRocket initialization options
 * @param {string} [options.appId] - LogRocket application ID
 * @param {boolean} [options.isReact=true] - Whether to enable React plugin
 * @throws {Error} When LogRocket appId is not provided or available
 */
const init = (options?: LROptions) => {
  const {
    appId,
    isReact = true,
    rootHostname,
    childDomains,
    parentDomain,
    ...rest
  } = options ?? {};
  lrAppId = getLRAppId(appId);
  if (!lrAppId) {
    throw new Error(
      'LogRocket appId is required to initialize LogRocket. Either pass it as parameter or set window.emui.logRocketConfig.appId',
    );
  }
  if (isLogRocketDisabled()) {
    window.emui.isLogRocketInitialized = false;
    window.emui.lrEnabled = false;
    getLogger().info('LogRocket is disabled in this environment');
    return;
  }
  window.emui.lrEnabled = true;
  const defaultOptions = getDefaultOptions(
    resolveAutoDetectedOverrides({ rootHostname, childDomains, parentDomain }),
  );
  const consent = hasUserConsentedToSessionRecording();
  window.emui.lrSessionRecordingConsent = consent;
  if (defaultOptions.dom) {
    // disable session recording based on user consent
    defaultOptions.dom.isEnabled = consent;
  }

  const finalLogRocketOptions: LROptions = deepMerge(
    defaultOptions,
    rest,
  ) as LROptions;
  LogRocketLib.init(lrAppId, finalLogRocketOptions);
  finishInitWithOptionalReactPlugin(
    isReact,
    finalLogRocketOptions.dom?.isEnabled === true,
  );
};

/**
 * Checks if the OneTrust consent banner has been closed by the user.
 * This is determined by checking for the presence of the 'OptanonAlertBoxClosed' cookie.
 * @returns {boolean} `true` if the banner has been closed, `false` otherwise.
 */
const hasSeenBanner = (): boolean => {
  const m = document.cookie.match(/(^|;\s*)OptanonAlertBoxClosed=([^;]+)/);
  if (!m) return false;
  try {
    return !!decodeURIComponent(m[2]);
  } catch {
    return false;
  }
};

/**
 * Initialize LogRocket with optional OneTrust integration
 *
 * This function handles LogRocket initialization with support for OneTrust consent management.
 * The initialization behavior depends on the `window.hasOneTrust` flag and consent banner state:
 * - If `window.hasOneTrust` is true and the consent banner hasn't been seen, waits for 'OTConsentApplied' event (until user interacts with banner)
 * - If `window.hasOneTrust` is true and the banner was already seen, initializes immediately
 * - If `window.hasOneTrust` is false or undefined, LogRocket initializes immediately
 * - Prevents duplicate initialization by checking `window.emui.isLogRocketInitialized`
 * @example
 * // With OneTrust (when window.hasOneTrust is true)
 * initLogRocket({
 *   appId: 'your-project/your-app',
 *   isReact: true,
 *   rootHostname: '.ice.com',
 *   childDomains: ['https://your-child-domain.com'],
 *   parentDomain: 'https://your-parent-domain.com',
 * });
 * @example
 * // Without OneTrust (when window.hasOneTrust is false/undefined)
 * initLogRocket({
 *   appId: 'your-project/your-app',
 * });
 * @param {LROptions} [options] - LogRocket initialization options
 * @param {string} [options.appId] - LogRocket application ID
 * @param {boolean} [options.isReact=true] - Whether the application uses React
 */
const initLogRocket = (options?: LROptions) => {
  if (window.emui.isLogRocketInitialized) return;
  if (!getLRAppId(options?.appId)) {
    throw new Error(
      'LogRocket appId is required to initialize LogRocket. Either pass it as parameter or set window.emui.logRocketConfig.appId',
    );
  }
  window.emui.isLogRocketInitialized = true;
  if (window.hasOneTrust) {
    // OneTrust is present, wait for user interaction or groups update before initializing
    let handled = false;
    const handler = () => {
      if (handled) return;
      handled = true;
      try {
        init(options);
      } catch (error) {
        window.emui.isLogRocketInitialized = false;
        getLogger().error({
          message: 'LogRocket initialization failed',
          exception: error as Error,
        });
      }
    };
    const addListenerWithTimeout = (event: string) => {
      window.addEventListener(event, handler, { once: true });
      setTimeout(() => {
        if (!handled) {
          getLogger().warn(
            `OneTrust event '${event}' not received within 10s, initializing LogRocket with current consent state`,
          );
          handler();
        }
      }, 10000);
    };
    if (hasSeenBanner()) {
      addListenerWithTimeout('OneTrustGroupsUpdated');
    } else if (getActiveConsentGroups().includes('C0002')) {
      // C0002 is already active (non-rejectable), init immediately
      handler();
    } else {
      addListenerWithTimeout('OTConsentApplied');
    }
  } else {
    // OneTrust not present, initialize immediately and asynchronously
    setTimeout(() => {
      try {
        init(options);
      } catch (error) {
        window.emui.isLogRocketInitialized = false;
        getLogger().error({
          message: 'LogRocket initialization failed',
          exception: error as Error,
        });
      }
    }, 0);
  }
};

export {
  initLogRocket,
  hasUserConsentedToSessionRecording,
  buildLogRocketQueryParams,
};
